import { useEffect, useState } from "react";
import { CalculationOutput } from "../types";
import { PeriodMode, fmtEUR, suffix } from "../utils/period";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";

type Tab = "irpf" | "iva" | "quarterly" | "compare";

export default function FiscalDetails({
  output,
  outputA,
  outputB,
  periodMode,
  isPremium = true,
  activeTab,
  onTabChange,
  showQuarterlyTab = true,
}: {
  output: CalculationOutput | null;
  outputA: CalculationOutput | null;
  outputB: CalculationOutput | null;
  periodMode: PeriodMode;
  isPremium?: boolean;
  activeTab?: Tab;
  onTabChange?: (tab: Tab) => void;
  showQuarterlyTab?: boolean;
}) {
  const [tab, setTab] = useState<Tab>(activeTab ?? "irpf");
  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab && activeTab !== tab) {
      setTab(activeTab);
    }
  }, [activeTab, tab]);

  useEffect(() => {
    if (!showQuarterlyTab && tab === "quarterly") {
      setTab("irpf");
      onTabChange?.("irpf");
    }
  }, [showQuarterlyTab, tab, onTabChange]);

  if (!output) return null;

  const tabs = [
    { id: "irpf", label: "🧾 IRPF" },
    { id: "iva", label: "💶 IVA y pagos" },
    ...(showQuarterlyTab ? [{ id: "quarterly", label: "📊 Trimestral" }] : []),
    { id: "compare", label: "🔄 Escenarios A/B" },
  ];

  return (
    <div
      style={{
        backgroundColor: "#fff",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {!isPremium && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.97)",
            backdropFilter: "blur(12px)",
            borderRadius: "var(--radius-lg)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
            padding: "64px 32px",
            textAlign: "center",
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: "80px",
              height: "80px",
              backgroundColor: "var(--brand-soft)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Lock size={40} color="var(--brand)" />
          </div>
          <div>
            <h3
              style={{
                margin: "0 0 12px",
                fontSize: "24px",
                fontWeight: 700,
                color: "var(--text)",
              }}
            >
              Detalles fiscales en Premium
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: "16px",
                color: "var(--muted)",
                maxWidth: "540px",
                lineHeight: "1.6",
              }}
            >
              Accede al detalle de IRPF, IVA, proyección trimestral, comparación de escenarios y análisis detallado.
            </p>
          </div>
          <button
            onClick={() => navigate('/login')}
            style={{
              appearance: "none",
              padding: "14px 32px",
              backgroundColor: "var(--brand)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              fontSize: "16px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s ease",
              marginTop: "8px",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--brand-ink)";
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "var(--brand)";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
            }}
          >
            Acceder a Premium
          </button>
        </div>
      )}
      <div
        style={{
          display: "flex",
          gap: "0",
          padding: "16px 24px",
          borderBottom: "1px solid var(--border)",
          backgroundColor: "#fafbfc",
          overflowX: "auto",
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setTab(t.id as Tab);
              onTabChange?.(t.id as Tab);
            }}
            style={{
              appearance: "none",
              background: "none",
              border: "none",
              padding: "8px 16px",
              fontSize: "13px",
              fontWeight: 600,
              color: tab === t.id ? "var(--brand-ink)" : "var(--muted)",
              cursor: "pointer",
              transition: "all 0.15s ease",
              borderBottom: tab === t.id ? "2px solid var(--brand)" : "2px solid transparent",
              marginBottom: "-16px",
              paddingBottom: "24px",
              whiteSpace: "nowrap",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ padding: "24px" }}>
        {tab === "irpf" && <IRPFTab output={output} periodMode={periodMode} />}
        {tab === "iva" && <IVATab output={output} periodMode={periodMode} />}
        {tab === "quarterly" && showQuarterlyTab && <QuarterlyTab output={output} />}
        {tab === "compare" && <CompareTab a={outputA} b={outputB} periodMode={periodMode} />}
      </div>
    </div>
  );
}

function IRPFTab({
  output,
  periodMode,
}: {
  output: CalculationOutput;
  periodMode: PeriodMode;
}) {
  const baseIRPF = output.baseIRPF ?? 0;
  const irpfTotal = (output as any).irpfTotal ?? output?.irpf?.cuotaTotal ?? 0;
  const antRet = (output as any).anticiposIRPF_retenciones ?? 0;
  const ant130 = (output as any).anticiposIRPF_mod130 ?? 0;
  const anticipos = antRet + ant130;
  const reg = Number((output as any).irpfRegularizacion ?? 0);
  const pendiente = Math.max(0, reg);
  const devolucion = Math.max(0, -reg);

  return (
    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "32px",
          minWidth: "480px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "var(--muted)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "16px",
            }}
          >
            Base imponible
          </div>
          <MetricRow
            label="Base IRPF"
            value={fmtEUR(baseIRPF, periodMode)}
            suffix={suffix(periodMode)}
          />
          {((output as any).minimoPersonalUsado ?? 0) > 0 && (
            <MetricRow
              label="Mínimo personal aplicado"
              value={fmtEUR((output as any).minimoPersonalUsado, periodMode)}
              suffix={suffix(periodMode)}
            />
          )}
          {((output as any).reduccionRendimiento ?? 0) > 0 && (
            <MetricRow
              label="Reducción general art. 32"
              value={fmtEUR((output as any).reduccionRendimiento, periodMode)}
              suffix={suffix(periodMode)}
            />
          )}
        </div>

        <div>
          <div
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "var(--muted)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "16px",
            }}
          >
            Cálculo IRPF
          </div>
          <MetricRow
            label="IRPF total"
            value={fmtEUR(irpfTotal, periodMode)}
            suffix={suffix(periodMode)}
            bold
          />
          <MetricRow
            label="Anticipos pagados"
            value={fmtEUR(anticipos, periodMode)}
            suffix={suffix(periodMode)}
          />
          <div style={{ borderTop: "2px solid var(--border)", marginTop: "16px", paddingTop: "16px" }}>
            {devolucion > 0 && (
              <MetricRow
                label="✅ Devolución"
                value={fmtEUR(devolucion, periodMode)}
                suffix={suffix(periodMode)}
                highlight="green"
                bold
              />
            )}
            {pendiente > 0 && (
              <MetricRow
                label="⚠️ Pendiente de pagar"
                value={fmtEUR(pendiente, periodMode)}
                suffix={suffix(periodMode)}
                highlight="red"
                bold
              />
            )}
            {pendiente === 0 && devolucion === 0 && (
              <div style={{ fontSize: "13px", color: "var(--muted)", textAlign: "center" }}>
                Liquidación en cero
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function IVATab({
  output,
  periodMode,
}: {
  output: CalculationOutput;
  periodMode: PeriodMode;
}) {
  const ivaInfo = output?.iva?.liquidacionAnual ?? 0;

  return (
    <div>
      <MetricRow
        label="Liquidación IVA anual"
        value={fmtEUR(Math.abs(ivaInfo), periodMode)}
        suffix={suffix(periodMode)}
        bold
      />
      <div
        style={{
          fontSize: "13px",
          color: "var(--muted)",
          marginTop: "12px",
          padding: "12px",
          backgroundColor: "#fafbfc",
          borderRadius: "8px",
        }}
      >
        {ivaInfo > 0 ? "💰 A pagar a Hacienda" : ivaInfo < 0 ? "✅ A devolver por Hacienda" : "Sin IVA pendiente"}
      </div>
    </div>
  );
}

export function QuarterlyTab({ output }: { output: CalculationOutput | null }) {
  if (!output?.quarterly) return <div>Sin datos trimestrales</div>;

  const fmtc = (n: number) =>
    n.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

  return (
    <div>
      <div className="desktop-only" style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "13px",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#fafbfc", borderBottom: "2px solid var(--border)" }}>
              {["Trim.", "Ingresos", "Gastos", "IVA", "Ret.", "130", "IRPF", "Neto"].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: h === "Trim." ? "left" : "right",
                    padding: "12px 8px",
                    fontWeight: 600,
                    color: "var(--text)",
                    fontSize: "12px",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {output.quarterly.qrows.map((r, idx) => {
              const ivaNet = r.liquidacionIVA;
              return (
                <tr
                  key={r.quarter}
                  style={{
                    borderBottom: "1px solid var(--border)",
                    backgroundColor: idx % 2 === 0 ? "transparent" : "#fafbfc",
                    transition: "background 0.15s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f1f5f9")}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "transparent" : "#fafbfc")
                  }
                >
                  <td style={{ padding: "12px 8px", fontWeight: 600 }}>Q{r.quarter}</td>
                  <td style={{ padding: "12px 8px", textAlign: "right" }}>{fmtc(r.ingresosImponibles)}</td>
                  <td style={{ padding: "12px 8px", textAlign: "right" }}>{fmtc(r.gastosDeducibles)}</td>
                  <td style={{ padding: "12px 8px", textAlign: "right" }}>{fmtc(ivaNet)}</td>
                  <td style={{ padding: "12px 8px", textAlign: "right" }}>{fmtc(r.retencionesSufridas)}</td>
                  <td style={{ padding: "12px 8px", textAlign: "right" }}>{fmtc(r.pagos130)}</td>
                  <td style={{ padding: "12px 8px", textAlign: "right" }}>{fmtc(r.irpfAnticipos)}</td>
                  <td style={{ padding: "12px 8px", textAlign: "right", fontWeight: 700, color: "var(--brand-ink)" }}>
                    {fmtc(r.netoCaja)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mobile-only">
        <div className="quarter-card-grid">
          {output.quarterly.qrows.map((r) => (
            <div key={r.quarter} className="quarter-card">
              <header>
                <span>Q{r.quarter}</span>
                <span style={{ color: "var(--brand-ink)", fontSize: "13px" }}>{fmtc(r.netoCaja)}</span>
              </header>
              <div className="quarter-pills">
                <div className="quarter-pill">
                  <span className="dot" style={{ background: "#3b82f6" }}></span>
                  <span>Ingresos {fmtc(r.ingresosImponibles)}</span>
                </div>
                <div className="quarter-pill">
                  <span className="dot" style={{ background: "#ef4444" }}></span>
                  <span>Gastos {fmtc(r.gastosDeducibles)}</span>
                </div>
                <div className="quarter-pill">
                  <span className="dot" style={{ background: "#22c55e" }}></span>
                  <span>IVA {fmtc(r.liquidacionIVA)}</span>
                </div>
                <div className="quarter-pill">
                  <span className="dot" style={{ background: "#0ea5e9" }}></span>
                  <span>Ret. {fmtc(r.retencionesSufridas)}</span>
                </div>
                <div className="quarter-pill">
                  <span className="dot" style={{ background: "#a855f7" }}></span>
                  <span>130 {fmtc(r.pagos130)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CompareTab({
  a,
  b,
  periodMode,
}: {
  a: CalculationOutput | null;
  b: CalculationOutput | null;
  periodMode: PeriodMode;
}) {
  if (!a || !b) return <div>Activa ambos escenarios para comparar</div>;

  const items = [
    { label: "Bruto facturado", a: (a as any).brutoFacturado, b: (b as any).brutoFacturado },
    { label: "Neto final", a: (a as any).netoFinalTrasRenta, b: (b as any).netoFinalTrasRenta },
    { label: "IRPF total", a: (a as any).irpfTotal, b: (b as any).irpfTotal },
    { label: "Cuota de autónomos anual", a: (a as any).cuotaRETAAnual, b: (b as any).cuotaRETAAnual },
  ];

  return (
    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <div style={{ minWidth: "520px" }}>
        {items.map((item) => {
          const diff = item.b - item.a;
          const pct = item.a ? ((diff / item.a) * 100).toFixed(1) : "—";
          return (
            <div
              key={item.label}
              style={{
                padding: "16px 0",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "8px" }}>
                {item.label}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "16px",
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontSize: "11px", color: "var(--muted)", marginBottom: "4px" }}>
                    Escenario A
                  </div>
                  <div style={{ fontWeight: 600 }}>{fmtEUR(item.a, periodMode)}{suffix(periodMode)}</div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", color: "var(--muted)", marginBottom: "4px" }}>
                    Escenario B
                  </div>
                  <div style={{ fontWeight: 600 }}>{fmtEUR(item.b, periodMode)}{suffix(periodMode)}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "11px", color: "var(--muted)", marginBottom: "4px" }}>
                    Diferencia
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      color: diff > 0 ? "#10b981" : diff < 0 ? "#ef4444" : "var(--muted)",
                    }}
                  >
                    {diff > 0 ? "+" : ""}{fmtEUR(diff, periodMode)} ({pct}%)
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MetricRow({
  label,
  value,
  suffix,
  bold,
  highlight,
}: {
  label: string;
  value: string;
  suffix?: string;
  bold?: boolean;
  highlight?: "green" | "red";
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        paddingBottom: "12px",
        marginBottom: "12px",
        borderBottom: "1px solid #f1f5f9",
      }}
    >
      <span style={{ fontSize: "13px", color: "var(--muted)" }}>{label}</span>
      <span
        style={{
          fontSize: bold ? "16px" : "14px",
          fontWeight: bold ? 700 : 600,
          color:
            highlight === "green"
              ? "#059669"
              : highlight === "red"
              ? "#dc2626"
              : "var(--text)",
          backgroundColor:
            highlight === "green"
              ? "#ecfdf5"
              : highlight === "red"
              ? "#fef2f2"
              : "transparent",
          padding: highlight ? "6px 10px" : "0",
          borderRadius: highlight ? "6px" : "0",
        }}
      >
        {value}
        {suffix}
      </span>
    </div>
  );
}
